import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './GpsExceptionsPage.css';
import {
  MapPin, RefreshCw, AlertTriangle, Users, Building2, Package, Search,
  ChevronDown, X, LogIn, LogOut, Check, Inbox, CheckCircle2, ArrowRight, Clock,
  Undo2, StickyNote,
} from 'lucide-react';
import { MapContainer, TileLayer, Circle, Marker, ZoomControl, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { bluePinIcon, ensureProjectMapMarkerIcon, MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM, MAP_TILE_URL } from '../../components/projectMapLeaflet';
import WeekPickerNav from '../../components/WeekPickerNav';
import { useSiteContext } from '../../SiteContext';
import { getPayPeriodDate } from '../../hooks/usePayPeriods';
import { AdminPunchException } from '../../interfaces/PunchExceptions';
import {
  getPunchExceptions, getPunchExceptionsErrorMessage,
  setPunchExceptionReviewed, setPunchExceptionReviewedErrorMessage,
  PunchExceptionReviewResult,
} from '../../api/punchExceptions';

// ── Types ────────────────────────────────────────────────────────────────────

type SortKey = 'time' | 'employee' | 'job' | 'distance';
type SideFilter = 'all' | 'in' | 'out';
type ReviewFilter = 'unreviewed' | 'reviewed' | 'all';

interface EnrichedException extends AdminPunchException {
  inDate: Date | null;
  outDate: Date | null;
  distMiles: number | null;
  hasCoords: boolean;
  repeat: boolean;
  hotJob: boolean;
  sortTime: number;
}

interface HotSiteInfo {
  projectID: number;
  count: number;
  name: string;
  altProjectID: string | null;
  radiusMiles: number | null;
}

interface DistFmt {
  v: string;
  tone: 'near' | 'far';
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const R_MILES = 3958.8;
function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDist(mi: number): DistFmt {
  if (mi < 0.19) {
    const feet = Math.round((mi * 5280) / 10) * 10;
    return { v: `${feet} ft`, tone: 'near' };
  }
  return { v: `${mi.toFixed(mi < 1 ? 2 : 1)} mi`, tone: mi >= 1 ? 'far' : 'near' };
}

/** Same offset-application convention as EmployeeTimesheetReviewConsole.tsx: shifts the
 *  UTC instant by the punch's own timezone offset, then all reads use UTC getters. */
function applyOffset(utcISO: string, offsetStr: string | null): Date {
  const base = new Date(utcISO);
  const m = (offsetStr ?? '').match(/([+-])(\d{2}):(\d{2})/);
  if (!m) return base;
  const sign = m[1] === '+' ? 1 : -1;
  const offMs = sign * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) * 60000;
  return new Date(base.getTime() + offMs);
}

function formatClock(d: Date): string {
  let h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const ap = h >= 12 ? 'p' : 'a';
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, '0')}${ap}`;
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatDateMono(d: Date): string {
  return `${DOW[d.getUTCDay()]} ${MON[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

/** Formats a review timestamp (server UTC ISO) in the viewer's local time for a tooltip. */
function formatReviewedAt(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h >= 12 ? 'p' : 'a';
  h = h % 12 || 12;
  return `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} ${h}:${m.toString().padStart(2, '0')}${ap}`;
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

/** Deterministic per-employee avatar hue (stable across rows/renders). */
function avatarColor(seed: string): string {
  let h = 7;
  for (let i = 0; i < seed.length; i++) h = (h * 33 + seed.charCodeAt(i)) & 0x7fffffff;
  return `hsl(${h % 360} 60% 42%)`;
}

// Punch-pin divIcons for the map modal — teardrop pins matching the design reference,
// green (clock-in) / amber (clock-out); the site pin reuses the app's shared bluePinIcon.
const greenPinIcon = L.divIcon({
  className: '',
  iconSize: [22, 22],
  iconAnchor: [13, 13],
  html: '<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#1f9d6b;border:2.5px solid #fff;box-shadow:0 2px 5px rgba(0,0,0,.35)"></div>',
});
const amberPinIcon = L.divIcon({
  className: '',
  iconSize: [22, 22],
  iconAnchor: [13, 13],
  html: '<div style="width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#d4881a;border:2.5px solid #fff;box-shadow:0 2px 5px rgba(0,0,0,.35)"></div>',
});

// ── Float menu (fixed-position, portalled — never position:absolute in a card/table) ──

interface FloatMenuOption {
  value: string;
  label: string;
  idLabel?: string | null;
  count?: number;
}

/**
 * Touch devices don't get autoFocus on the filter box: React's autoFocus fires in the commit
 * phase, which on iOS Safari can land outside the user-gesture window that allows raising the
 * keyboard, so it appears and is immediately dismissed. Tapping the field always works.
 */
const isCoarsePointer = () =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;

const FM_GAP = 6;
const FM_EDGE = 12;
/** FM_LIST_MAX matches `.gpx-floatmenu .fm-list { max-height }` in the CSS. */
const FM_LIST_MAX = 300;
const FM_LIST_MIN = 120;

interface FloatMenuButtonProps {
  label: string;
  valueLabel: string;
  isPlaceholder: boolean;
  isActive: boolean;
  options: FloatMenuOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  /** Type-to-filter search header (long lists: Employee / Job / Supervisor). */
  searchable?: boolean;
}

const FloatMenuButton: React.FC<FloatMenuButtonProps> = ({
  label, valueLabel, isPlaceholder, isActive, options, selectedValue, onSelect, searchable,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; listMaxH: number } | null>(null);
  // In-menu type-to-filter (client-side over already-loaded options) + keyboard
  // highlight. Both reset on every open — the typed text never persists.
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);

  useEffect(() => {
    if (open) { setQuery(''); setHi(0); }
  }, [open]);
  useEffect(() => { setHi(0); }, [query]);

  const q = query.trim().toLowerCase();
  // Match label + secondary id (users search by number too); "All …" matches "all".
  const visible = !searchable || !q
    ? options
    : options.filter((o) => `${o.label} ${o.idLabel ?? ''}`.toLowerCase().includes(q));

  const pick = (o: FloatMenuOption) => { onSelect(o.value); setOpen(false); };

  const onSearchKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHi((h) => (visible.length ? (h + 1) % visible.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHi((h) => (visible.length ? (h - 1 + visible.length) % visible.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (visible[hi]) pick(visible[hi]);
    }
    // Escape is handled by the document-level listener (closes the menu).
  };

  // Keep the keyboard-highlighted option visible inside the scrolling list — scrolling the
  // list directly, never scrollIntoView(), which can scroll the document instead and (on
  // iOS) shift the viewport out from under the open menu.
  useEffect(() => {
    if (!open) return;
    const list = menuRef.current?.querySelector('.fm-list') as HTMLElement | null;
    const el = list?.querySelector('.fm-opt.hl') as HTMLElement | null;
    if (!list || !el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [hi, open]);

  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const width = Math.max(r.width, 240);
    let left = r.left;
    if (left + width > window.innerWidth - FM_EDGE) left = Math.max(FM_EDGE, window.innerWidth - FM_EDGE - width);

    // visualViewport tracks the on-screen keyboard on iOS; window.innerHeight does not.
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const spaceBelow = vh - r.bottom - FM_GAP - FM_EDGE;
    const spaceAbove = r.top - FM_GAP - FM_EDGE;
    const below = spaceBelow >= spaceAbove;
    const avail = Math.max(below ? spaceBelow : spaceAbove, FM_LIST_MIN);

    const menuEl = menuRef.current;
    const listEl = menuEl?.querySelector('.fm-list') as HTMLElement | null;
    const chrome = menuEl && listEl ? menuEl.offsetHeight - listEl.offsetHeight : 56;
    const listMaxH = Math.min(FM_LIST_MAX, Math.max(FM_LIST_MIN, Math.floor(avail) - chrome));
    const menuH = menuEl ? Math.min(menuEl.offsetHeight, chrome + listMaxH) : chrome + listMaxH;

    const top = below ? r.bottom + FM_GAP : Math.max(FM_EDGE, r.top - FM_GAP - menuH);

    setPos((p) => (p && p.top === top && p.left === left && p.width === width && p.listMaxH === listMaxH
      ? p
      : { top, left, width, listMaxH }));
  }, []);

  // visible.length: filtering changes the menu's height, and a menu flipped above the trigger
  // is anchored by its bottom edge — without this it drifts away as the list shrinks.
  useLayoutEffect(() => { if (open) place(); }, [open, place, visible.length]);

  // The menu only renders once `pos` exists, so the first place() has no menu to measure.
  // Re-place as the node attaches, now that its real height is known.
  const setMenuNode = useCallback((node: HTMLDivElement | null) => {
    menuRef.current = node;
    if (node) place();
  }, [place]);

  // Scroll/resize re-anchor the menu. They used to close it, which on iOS Safari was
  // self-cancelling: focusing the filter box raises the keyboard, Safari scrolls to reveal
  // the field, and that scroll unmounted the field it was scrolling to.
  useEffect(() => {
    if (!open) return;
    // pointerdown, not mousedown: iOS only synthesises mouse events for elements it treats
    // as tappable, so a tap on plain background wouldn't close the menu.
    const onDocDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const vv = window.visualViewport;
    document.addEventListener('pointerdown', onDocDown);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    vv?.addEventListener('resize', place);
    vv?.addEventListener('scroll', place);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDocDown);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      vv?.removeEventListener('resize', place);
      vv?.removeEventListener('scroll', place);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place]);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`fsel${isActive ? ' active' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="fs-lab">{label}</span>
        <span className={`fs-val${isPlaceholder ? ' placeholder' : ''}`}>{valueLabel}</span>
        <ChevronDown className="caret" />
      </button>
      {open && pos && ReactDOM.createPortal(
        <div
          ref={setMenuNode}
          className="gpx-floatmenu"
          style={{ top: pos.top, left: pos.left, width: pos.width }}
        >
          {searchable && (
            <div className="fm-search">
              <Search className="ic" />
              <input
                type="text"
                placeholder="Type to filter…"
                value={query}
                autoFocus={!isCoarsePointer()}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                enterKeyHint="done"
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onSearchKey}
              />
            </div>
          )}
          <div className="fm-list" style={{ maxHeight: pos.listMaxH }}>
            {visible.length === 0 ? (
              <div className="fm-empty">No matches</div>
            ) : (
              visible.map((o, i) => (
                <button
                  key={o.value}
                  type="button"
                  className={`fm-opt${o.value === selectedValue ? ' sel' : ''}${searchable && i === hi ? ' hl' : ''}`}
                  onClick={() => pick(o)}
                  onMouseEnter={searchable ? () => setHi(i) : undefined}
                >
                  <span>
                    {o.label}
                    {o.idLabel ? <span className="fm-id"> {o.idLabel}</span> : null}
                  </span>
                  {o.count != null ? <span className="fm-cnt">{o.count}</span> : <Check className="fm-check" />}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// ── Map modal ────────────────────────────────────────────────────────────────

function MapBoundsAdjuster({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    // Leaflet measures its container on mount, which is before the modal's flex
    // layout has settled — without invalidateSize the map renders blank/gray.
    const t = setTimeout(() => {
      map.invalidateSize();
      if (points.length >= 2) {
        map.fitBounds(L.latLngBounds(points).pad(0.35));
      } else if (points.length === 1) {
        map.setView(points[0], 14);
      }
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

interface Incident {
  side: 'in' | 'out';
  label: string;
  time: string | null;
  message: string | null;
  distMiles: number | null;
}

function GpsExceptionMapModal({
  row, onClose, onSubmitReview,
}: {
  row: EnrichedException;
  onClose: () => void;
  /** Round-trips the review PATCH and updates the parent's rawRows; rejects on failure. */
  onSubmitReview: (id: number, reviewed: boolean, note: string | null) => Promise<void>;
}) {
  useEffect(() => {
    ensureProjectMapMarkerIcon();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Note only applies when marking reviewed; un-review discards it (decision 5).
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  // Un-review always requires an inline confirm ("Yes, discard note" / "Cancel")
  // before the actual PATCH fires — reset whenever the modal is showing a new row.
  const [confirmingUndo, setConfirmingUndo] = useState(false);

  useEffect(() => {
    setConfirmingUndo(false);
  }, [row.punchExceptionID]);

  const handleReviewSubmit = async (reviewed: boolean) => {
    setSubmitting(true);
    setReviewError(null);
    try {
      await onSubmitReview(row.punchExceptionID, reviewed, reviewed ? (note.trim() || null) : null);
      setNote('');
      setConfirmingUndo(false);
    } catch (e) {
      setReviewError(setPunchExceptionReviewedErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const hasSite = row.siteLatitude != null && row.siteLongitude != null;
  const points: [number, number][] = [];
  if (hasSite) points.push([row.siteLatitude as number, row.siteLongitude as number]);
  const hasInPin = row.inException && row.inLatitude != null && row.inLongitude != null;
  const hasOutPin = row.outException && row.outLatitude != null && row.outLongitude != null;
  if (hasInPin) points.push([row.inLatitude as number, row.inLongitude as number]);
  if (hasOutPin) points.push([row.outLatitude as number, row.outLongitude as number]);
  const center: [number, number] = points[0] ?? [0, 0];

  const incidents: Incident[] = [];
  if (row.inException) {
    const dist = hasSite && row.inLatitude != null && row.inLongitude != null
      ? haversineMiles(row.siteLatitude as number, row.siteLongitude as number, row.inLatitude, row.inLongitude)
      : null;
    incidents.push({ side: 'in', label: 'Clock In', time: row.inDate ? formatClock(row.inDate) : null, message: row.inExceptionMessage, distMiles: dist });
  }
  if (row.outException) {
    const dist = hasSite && row.outLatitude != null && row.outLongitude != null
      ? haversineMiles(row.siteLatitude as number, row.siteLongitude as number, row.outLatitude, row.outLongitude)
      : null;
    incidents.push({ side: 'out', label: 'Clock Out', time: row.outDate ? formatClock(row.outDate) : null, message: row.outExceptionMessage, distMiles: dist });
  }

  const punchDate = row.inDate ?? row.outDate;

  return (
    <div className="gpx-modal-bg" onClick={onClose}>
      <div className="gpx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="gm-head">
          <div className="mi"><MapPin size={22} /></div>
          <div>
            <h3>{row.employeeName}</h3>
            <div className="gm-sub">
              <span className="eid mono">{row.altEmployeeID ?? '—'}</span>
              <span>·</span>
              <span>{row.jobTitle || '—'}</span>
              <span>·</span>
              <span className="mono">{punchDate ? formatDateMono(punchDate) : '—'}</span>
            </div>
          </div>
          <button type="button" className="x" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="gm-cols">
        <div className="gm-map">
          {points.length > 0 ? (
            <MapContainer center={center} zoom={14} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true} zoomControl={false}>
              <ZoomControl position="topright" />
              <TileLayer
                url={MAP_TILE_URL}
                maxZoom={MAP_TILE_MAX_ZOOM}
                attribution={MAP_TILE_ATTRIBUTION}
              />
              {hasSite && (
                <Circle
                  center={[row.siteLatitude as number, row.siteLongitude as number]}
                  radius={(row.radiusMiles ?? 0.1) * 1609.34}
                  pathOptions={{ color: '#2f6fed', weight: 2, dashArray: '6 4', fillOpacity: 0.06 }}
                />
              )}
              {hasSite && <Marker position={[row.siteLatitude as number, row.siteLongitude as number]} icon={bluePinIcon} />}
              {hasInPin && <Marker position={[row.inLatitude as number, row.inLongitude as number]} icon={greenPinIcon} />}
              {hasOutPin && <Marker position={[row.outLatitude as number, row.outLongitude as number]} icon={amberPinIcon} />}
              <MapBoundsAdjuster points={points} />
            </MapContainer>
          ) : (
            <div className="gm-nomap">
              <MapPin />
              <p>No coordinates available for this punch.</p>
            </div>
          )}

          {points.length > 0 && (
            <div className="gm-legend">
              {hasSite && <span className="lg"><span className="sw site" />Job site</span>}
              {hasSite && <span className="lg"><span className="sw ring" />Geofence</span>}
              {hasInPin && <span className="lg"><span className="sw pin-in" />Clock in</span>}
              {hasOutPin && <span className="lg"><span className="sw pin-out" />Clock out</span>}
            </div>
          )}
        </div>

        <div className="gm-body">
          {incidents.map((inc) => {
            const f = inc.distMiles != null ? formatDist(inc.distMiles) : null;
            return (
              <div key={inc.side} className="gm-inc">
                <div className="inc-head">
                  <span className={`sbadge ${inc.side}`}>
                    {inc.side === 'in' ? <LogIn /> : <LogOut />}
                    {inc.label}
                  </span>
                  {f && <span className={`inc-dist ${f.tone} mono`}>{f.v} from site</span>}
                </div>
                <div className="inc-msg">
                  {inc.message ?? <span className="muted">No message recorded.</span>}
                </div>
                {inc.time && <div className="inc-time mono">{inc.time}</div>}
              </div>
            );
          })}

          <div className="gm-meta">
            {hasSite ? (
              <>
                <div className="m"><span className="lab">Job site</span><b>{row.projectName ?? '—'}</b> <span className="mono">{row.altProjectID ?? ''}</span></div>
                <div className="m"><span className="lab">Allowed radius</span><b>{row.radiusMiles ?? '—'} mi</b></div>
              </>
            ) : (
              <div className="m">
                <span className="lab">Job site</span>
                <b>—</b> {row.projectID == null ? 'piece work, no geofence' : 'site location unknown'}
              </div>
            )}
            <div className="m"><span className="lab">Entry kind</span><b>{row.entryKind === 'hourly' ? 'Hourly' : 'Task / Piece'}</b></div>
          </div>

          {/* Review control — full form with optional note (decision 6) */}
          <div className="gm-review">
            <div className="gm-review-head">
              <span className={`rev-pill ${row.reviewed ? 'yes' : 'no'}`}>
                {row.reviewed ? <><Check />Reviewed{row.reviewNote && <StickyNote />}</> : 'Unreviewed'}
              </span>
              {row.reviewed && (row.reviewedByName || row.reviewedAt) && (
                <span className="gm-review-by">
                  {row.reviewedByName ?? 'Unknown'}
                  {row.reviewedAt ? ` · ${formatReviewedAt(row.reviewedAt)}` : ''}
                </span>
              )}
            </div>

            {reviewError && <div className="gm-review-err"><AlertTriangle />{`Couldn't save: ${reviewError}`}</div>}

            {row.reviewed ? (
              <>
                {row.reviewNote && (
                  <div className="gm-review-note">
                    <span className="lab">Note</span>
                    {row.reviewNote}
                  </div>
                )}
                {confirmingUndo ? (
                  <div className="gm-review-foot gm-undo-confirm">
                    <span className="gm-uc-txt">Undo review?</span>
                    <button
                      type="button"
                      className="btn gm-uc-cancel"
                      onClick={() => setConfirmingUndo(false)}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn rev-undo-yes"
                      onClick={() => handleReviewSubmit(false)}
                      disabled={submitting}
                    >
                      {submitting ? 'Working…' : 'Yes, discard note'}
                    </button>
                  </div>
                ) : (
                  <div className="gm-review-foot">
                    <button
                      type="button"
                      className="btn rev-undo"
                      onClick={() => setConfirmingUndo(true)}
                      disabled={submitting}
                    >
                      <Undo2 />Un-review
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <label className="gm-review-lab">
                  Review note <span className="opt">— optional</span>
                </label>
                <textarea
                  className="gm-review-ta"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add context for this review…"
                  rows={3}
                  maxLength={1000}
                  disabled={submitting}
                />
                <div className="gm-review-foot">
                  <button
                    type="button"
                    className="btn rev-mark"
                    onClick={() => handleReviewSubmit(true)}
                    disabled={submitting}
                  >
                    <Check />{submitting ? 'Saving…' : 'Mark reviewed'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

// ── Quick "Mark reviewed" popup (§2f) ────────────────────────────────────────
// Opened by the Reviewed-column checkmark on any unreviewed row — including rows
// with no coordinates, which cannot open the map modal. Lighter than the map
// modal; carries the optional review note.

function MarkReviewedPopup({
  row, onClose, onSubmit,
}: {
  row: EnrichedException;
  onClose: () => void;
  /** Round-trips PATCH reviewed:true with the note; rejects on failure. */
  onSubmit: (id: number, note: string | null) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    taRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      // Empty textarea → note:null, identical to a note-less mark.
      await onSubmit(row.punchExceptionID, note.trim() || null);
      onClose();
    } catch (e) {
      // Keep the typed note; just surface the error and re-enable the button.
      setError(setPunchExceptionReviewedErrorMessage(e));
      setSaving(false);
    }
  };

  const punchDate = row.inDate ?? row.outDate;

  return (
    <div className="gpx-quickpop-bg" onClick={onClose}>
      <div className="gpx-quickpop" onClick={(e) => e.stopPropagation()}>
        <div className="qp-head">
          <div className="qp-ic"><CheckCircle2 /></div>
          <div>
            <h3>Mark reviewed</h3>
            <div className="qp-sub">
              <span>{row.employeeName}</span>
              <span>·</span>
              <span className="mono">{row.altEmployeeID ?? '—'}</span>
              <span>·</span>
              <span className="mono">{punchDate ? formatDateMono(punchDate) : '—'}</span>
            </div>
          </div>
          <button type="button" className="x" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="qp-body">
          <label className="qp-lab" htmlFor="gpx-qp-note">Note (optional)</label>
          <textarea
            id="gpx-qp-note"
            ref={taRef}
            className="qp-ta"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (optional) — e.g. confirmed with the employee, bad geofence, no action needed…"
            maxLength={1000}
            disabled={saving}
          />
          {error && <div className="qp-err"><AlertTriangle />{`Couldn't save: ${error}`}</div>}
        </div>
        <div className="qp-foot">
          <button type="button" className="btn qp-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn rev-mark" onClick={submit} disabled={saving}>
            <Check />{saving ? 'Saving…' : 'Mark reviewed'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Loading skeleton ─────────────────────────────────────────────────────────

function KpiSkeleton() {
  return (
    <div className="kpis skel-kpis">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="kpi">
          <span className="sk-line sk-sm" />
          <span className="sk-line sk-lg" style={{ marginTop: 10 }} />
        </div>
      ))}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="table-card">
      <div className="skel-rows">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skel-row">
            <span className="sk-line sk-avatar" />
            <span className="sk-line sk-md" />
            <span className="sk-line sk-sm" />
            <span className="sk-line sk-sm" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const GpsExceptionsPage: React.FC = () => {
  const {
    payPeriods, payPeriodID,
    setWeek, setYear, setPayPeriodID, setStartDate, setEndDate,
  } = useSiteContext();

  const selectedIdx = payPeriods.findIndex((pp) => pp.PayPeriodID === payPeriodID);

  const [rawRows, setRawRows] = useState<AdminPunchException[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [locFilter, setLocFilter] = useState<number | '__none' | null>(null);
  const [supFilter, setSupFilter] = useState<number | '__none' | null>(null);
  const [empFilter, setEmpFilter] = useState<number | null>(null);
  const [jobFilter, setJobFilter] = useState<number | '__piece' | null>(null);
  const [sideFilter, setSideFilter] = useState<SideFilter>('all');
  // Unlike the other filters (null = off), 'unreviewed' is a meaningful default that
  // hides already-reviewed rows so the page reads as a work queue (decision 1).
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('unreviewed');
  const [sortKey, setSortKey] = useState<SortKey>('time');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Keyed by punchExceptionID: which inline toggles are mid-PATCH / last errored.
  const [reviewPending, setReviewPending] = useState<Record<number, boolean>>({});
  const [reviewRowError, setReviewRowError] = useState<Record<number, string>>({});
  const [mapRowID, setMapRowID] = useState<number | null>(null);
  // Which row's quick "Mark reviewed" popup (§2f) is open — the row checkmark opens
  // this note-capable popup rather than instant-marking.
  const [notePopupID, setNotePopupID] = useState<number | null>(null);
  // Which row's Reviewed toggle is showing the inline "Undo review? · Yes / Cancel"
  // confirm — un-review always requires this extra step, marking reviewed does not.
  const [confirmUndoID, setConfirmUndoID] = useState<number | null>(null);

  /** Deps intentionally omit SiteContext functions — unstable refs would retrigger
   *  this effect every render (infinite loading loop; see project memory). */
  const load = useCallback(async () => {
    if (!payPeriodID) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getPunchExceptions(payPeriodID);
      setRawRows(data);
    } catch (e) {
      setLoadError(getPunchExceptionsErrorMessage(e));
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [payPeriodID]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPeriodID]);

  // Filters are period-scoped — stale selections from a previous period would silently
  // render a filtered-to-zero table, so reset them whenever the period changes.
  useEffect(() => {
    setLocFilter(null);
    setSupFilter(null);
    setEmpFilter(null);
    setJobFilter(null);
    setSideFilter('all');
    // Resets to the 'unreviewed' default (not a neutral/off value) — see the state comment.
    setReviewFilter('unreviewed');
    setSearch('');
  }, [payPeriodID]);

  const handlePeriodChange = (idx: number) => {
    const pp = payPeriods[idx];
    if (!pp) return;
    setWeek(pp.WeekNumber);
    setYear(String(pp.AccountingYear));
    setPayPeriodID(pp.PayPeriodID);
    setStartDate(getPayPeriodDate(new Date(pp.StartDate)));
    setEndDate(getPayPeriodDate(new Date(pp.EndDate)));
  };

  // ── Enrichment (period-wide; filters never touch this) ─────────────────────
  const enriched = useMemo<EnrichedException[]>(() => {
    const empCounts = new Map<number, number>();
    const jobEmpSets = new Map<number, Set<number>>();
    rawRows.forEach((r) => {
      empCounts.set(r.employeeID, (empCounts.get(r.employeeID) ?? 0) + 1);
      if (r.projectID != null) {
        if (!jobEmpSets.has(r.projectID)) jobEmpSets.set(r.projectID, new Set());
        jobEmpSets.get(r.projectID)!.add(r.employeeID);
      }
    });
    return rawRows.map((r) => {
      const inDate = r.inDateTime ? applyOffset(r.inDateTime, r.inDateTimeOffset) : null;
      const outDate = r.outDateTime ? applyOffset(r.outDateTime, r.outDateTimeOffset) : null;
      const hasSite = r.siteLatitude != null && r.siteLongitude != null;
      const dists: number[] = [];
      if (r.inException && hasSite && r.inLatitude != null && r.inLongitude != null) {
        dists.push(haversineMiles(r.siteLatitude as number, r.siteLongitude as number, r.inLatitude, r.inLongitude));
      }
      if (r.outException && hasSite && r.outLatitude != null && r.outLongitude != null) {
        dists.push(haversineMiles(r.siteLatitude as number, r.siteLongitude as number, r.outLatitude, r.outLongitude));
      }
      const hasCoords = (r.inException && r.inLatitude != null && r.inLongitude != null)
        || (r.outException && r.outLatitude != null && r.outLongitude != null);
      return {
        ...r,
        inDate, outDate,
        distMiles: dists.length ? Math.max(...dists) : null,
        hasCoords,
        repeat: (empCounts.get(r.employeeID) ?? 0) > 1,
        hotJob: r.projectID != null && (jobEmpSets.get(r.projectID)?.size ?? 0) >= 3,
        sortTime: (inDate ?? outDate)?.getTime() ?? 0,
      };
    });
  }, [rawRows]);

  // Modal reads its row live from `enriched` (keyed by id) so it reflects a fresh
  // review state after an in-place rawRows update, rather than a stale captured copy.
  const mapRow = useMemo<EnrichedException | null>(
    () => (mapRowID == null ? null : enriched.find((r) => r.punchExceptionID === mapRowID) ?? null),
    [enriched, mapRowID]
  );

  const notePopupRow = useMemo<EnrichedException | null>(
    () => (notePopupID == null ? null : enriched.find((r) => r.punchExceptionID === notePopupID) ?? null),
    [enriched, notePopupID]
  );

  // ── Review actions (in-place rawRows update, no refetch) ────────────────────
  const applyReviewResult = useCallback((result: PunchExceptionReviewResult) => {
    setRawRows((prev) => prev.map((r) => (r.punchExceptionID === result.punchExceptionID
      ? {
        ...r,
        reviewed: result.reviewed,
        reviewedByAdminEmployeeID: result.reviewedByAdminEmployeeID,
        reviewedByName: result.reviewedByName,
        reviewedAt: result.reviewedAt,
        reviewNote: result.reviewNote,
      }
      : r)));
  }, []);

  // Inline un-review path (fired from the row's confirmed "Undo review?"); marking
  // reviewed goes through the quick popup / modal instead. Tracks per-row
  // pending/error locally.
  const toggleReviewInline = useCallback(async (id: number, currentlyReviewed: boolean) => {
    setReviewPending((p) => ({ ...p, [id]: true }));
    setReviewRowError((p) => { const n = { ...p }; delete n[id]; return n; });
    try {
      const result = await setPunchExceptionReviewed(id, !currentlyReviewed);
      applyReviewResult(result);
    } catch (e) {
      setReviewRowError((p) => ({ ...p, [id]: setPunchExceptionReviewedErrorMessage(e) }));
    } finally {
      setReviewPending((p) => { const n = { ...p }; delete n[id]; return n; });
    }
  }, [applyReviewResult]);

  // Modal full-path submit (may carry a note); modal owns its own submitting/error UI.
  const submitReview = useCallback(async (id: number, reviewed: boolean, note: string | null) => {
    const result = await setPunchExceptionReviewed(id, reviewed, note);
    applyReviewResult(result);
  }, [applyReviewResult]);

  // ── KPIs (period-wide, static — never filtered, per README §4) ──────────────
  const totalExceptions = rawRows.length;
  // Period-wide review counts (from rawRows, so filters never change them).
  const unreviewedCount = useMemo(() => rawRows.filter((r) => !r.reviewed).length, [rawRows]);
  const reviewedCount = totalExceptions - unreviewedCount;
  const employeesAffected = useMemo(() => new Set(rawRows.map((r) => r.employeeID)).size, [rawRows]);
  const jobSitesAffected = useMemo(
    () => new Set(rawRows.filter((r) => r.projectID != null).map((r) => r.projectID)).size,
    [rawRows]
  );
  const hasPieceWork = useMemo(() => rawRows.some((r) => r.projectID == null), [rawRows]);
  // One punch can flag both sides, so in + out can exceed the total — the tile notes this.
  const clockInCount = useMemo(() => rawRows.filter((r) => r.inException).length, [rawRows]);
  const clockOutCount = useMemo(() => rawRows.filter((r) => r.outException).length, [rawRows]);

  const hotSite = useMemo<HotSiteInfo | null>(() => {
    const byJob = new Map<number, Set<number>>();
    rawRows.forEach((r) => {
      if (r.projectID != null) {
        if (!byJob.has(r.projectID)) byJob.set(r.projectID, new Set());
        byJob.get(r.projectID)!.add(r.employeeID);
      }
    });
    let bestProjectID: number | null = null;
    let bestCount = 0;
    for (const [pid, set] of byJob) {
      if (set.size >= 3 && set.size > bestCount) {
        bestProjectID = pid;
        bestCount = set.size;
      }
    }
    if (bestProjectID == null) return null;
    const row = rawRows.find((r) => r.projectID === bestProjectID);
    if (!row) return null;
    return {
      projectID: bestProjectID,
      count: bestCount,
      name: row.projectName ?? '—',
      altProjectID: row.altProjectID,
      radiusMiles: row.radiusMiles,
    };
  }, [rawRows]);

  // ── Filter option lists (period-wide counts, per README §3) ─────────────────
  const locationOptions = useMemo(() => {
    const map = new Map<string, { label: string; count: number }>();
    rawRows.forEach((r) => {
      const key = r.locationID != null ? String(r.locationID) : '__none';
      const cur = map.get(key);
      if (cur) cur.count += 1;
      else map.set(key, { label: r.locationID != null ? (r.locationName ?? `Location ${r.locationID}`) : 'No location', count: 1 });
    });
    return Array.from(map.entries())
      .map(([value, v]) => ({ value, ...v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rawRows]);

  const supervisorOptions = useMemo(() => {
    const map = new Map<string, { label: string; count: number }>();
    rawRows.forEach((r) => {
      const key = r.supervisorID != null ? String(r.supervisorID) : '__none';
      const cur = map.get(key);
      if (cur) cur.count += 1;
      else map.set(key, { label: r.supervisorID != null ? (r.supervisorName ?? `Supervisor ${r.supervisorID}`) : 'No supervisor', count: 1 });
    });
    return Array.from(map.entries())
      .map(([value, v]) => ({ value, ...v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rawRows]);

  const employeeOptions = useMemo(() => {
    const map = new Map<number, { name: string; altID: string | null; count: number }>();
    rawRows.forEach((r) => {
      const cur = map.get(r.employeeID);
      if (cur) cur.count += 1;
      else map.set(r.employeeID, { name: r.employeeName, altID: r.altEmployeeID, count: 1 });
    });
    return Array.from(map.entries())
      .map(([employeeID, v]) => ({ employeeID, ...v }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawRows]);

  const { jobOptions, pieceCount } = useMemo(() => {
    const map = new Map<number, { name: string; altID: string | null; count: number }>();
    let piece = 0;
    rawRows.forEach((r) => {
      if (r.projectID == null) { piece += 1; return; }
      const cur = map.get(r.projectID);
      if (cur) cur.count += 1;
      else map.set(r.projectID, { name: r.projectName ?? '—', altID: r.altProjectID, count: 1 });
    });
    const jobs = Array.from(map.entries())
      .map(([projectID, v]) => ({ projectID, ...v }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { jobOptions: jobs, pieceCount: piece };
  }, [rawRows]);

  // ── Filter + sort (client-side, table only) ─────────────────────────────────
  const filteredRows = useMemo(() => {
    let rows = enriched;
    if (locFilter != null) {
      rows = rows.filter((r) => (locFilter === '__none' ? r.locationID == null : r.locationID === locFilter));
    }
    if (supFilter != null) {
      rows = rows.filter((r) => (supFilter === '__none' ? r.supervisorID == null : r.supervisorID === supFilter));
    }
    if (empFilter != null) rows = rows.filter((r) => r.employeeID === empFilter);
    if (jobFilter === '__piece') rows = rows.filter((r) => r.projectID == null);
    else if (jobFilter != null) rows = rows.filter((r) => r.projectID === jobFilter);
    if (sideFilter === 'in') rows = rows.filter((r) => r.inException);
    else if (sideFilter === 'out') rows = rows.filter((r) => r.outException);
    if (reviewFilter === 'unreviewed') rows = rows.filter((r) => !r.reviewed);
    else if (reviewFilter === 'reviewed') rows = rows.filter((r) => r.reviewed);
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        r.employeeName.toLowerCase().includes(q) ||
        (r.altEmployeeID ?? '').toLowerCase().includes(q) ||
        (r.projectName ?? '').toLowerCase().includes(q) ||
        (r.inExceptionMessage ?? '').toLowerCase().includes(q) ||
        (r.outExceptionMessage ?? '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      switch (sortKey) {
        case 'employee': return a.employeeName.localeCompare(b.employeeName) * dir;
        case 'job': return (a.projectName ?? '￿').localeCompare(b.projectName ?? '￿') * dir;
        case 'distance': return ((a.distMiles ?? -1) - (b.distMiles ?? -1)) * dir;
        default: return (a.sortTime - b.sortTime) * dir;
      }
    });
  }, [enriched, locFilter, supFilter, empFilter, jobFilter, sideFilter, reviewFilter, search, sortKey, sortDir]);

  // reviewFilter only counts as "active" when it deviates from its 'unreviewed' default
  // (its default already hides rows, unlike the other filters whose null = off).
  const anyFilterActive = locFilter != null || supFilter != null || empFilter != null || jobFilter != null
    || sideFilter !== 'all' || reviewFilter !== 'unreviewed' || search.trim() !== '';

  // Everything anyFilterActive checks except the review filter — used to tell whether
  // the review filter is the *sole* reason filteredRows is empty (states §6).
  const otherFiltersActive = locFilter != null || supFilter != null || empFilter != null || jobFilter != null
    || sideFilter !== 'all' || search.trim() !== '';

  const clearFilters = () => {
    setLocFilter(null);
    setSupFilter(null);
    setEmpFilter(null);
    setJobFilter(null);
    setSideFilter('all');
    // Clearing returns reviewFilter to its 'unreviewed' default, not a neutral value.
    setReviewFilter('unreviewed');
    setSearch('');
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'time' ? 'desc' : 'asc');
    }
  };

  const sortCaretClass = (key: SortKey) => (sortKey === key ? ` sort-${sortDir}` : '');

  const locValueLabel = locFilter == null
    ? 'All locations'
    : locationOptions.find((o) => o.value === String(locFilter))?.label ?? 'All locations';
  const supValueLabel = supFilter == null
    ? 'All supervisors'
    : supervisorOptions.find((o) => o.value === String(supFilter))?.label ?? 'All supervisors';
  const empValueLabel = empFilter == null
    ? 'All employees'
    : employeeOptions.find((o) => o.employeeID === empFilter)?.name ?? 'All employees';
  const jobValueLabel = jobFilter == null
    ? 'All jobs'
    : jobFilter === '__piece'
      ? 'Piece work (no job)'
      : jobOptions.find((o) => o.projectID === jobFilter)?.name ?? 'All jobs';

  const locMenuOptions: FloatMenuOption[] = [
    { value: '', label: 'All locations' },
    ...locationOptions.map((o) => ({ value: o.value, label: o.label, count: o.count })),
  ];
  const supMenuOptions: FloatMenuOption[] = [
    { value: '', label: 'All supervisors' },
    ...supervisorOptions.map((o) => ({ value: o.value, label: o.label, count: o.count })),
  ];
  const empMenuOptions: FloatMenuOption[] = [
    { value: '', label: 'All employees' },
    ...employeeOptions.map((o) => ({ value: String(o.employeeID), label: o.name, idLabel: o.altID, count: o.count })),
  ];
  const jobMenuOptions: FloatMenuOption[] = [
    { value: '', label: 'All jobs' },
    ...jobOptions.map((o) => ({ value: String(o.projectID), label: o.name, idLabel: o.altID, count: o.count })),
    ...(pieceCount > 0 ? [{ value: '__piece', label: 'Piece work (no job)', count: pieceCount }] : []),
  ];

  const showSkeleton = loading && !hasLoadedOnce;
  const showRefreshing = loading && hasLoadedOnce;

  return (
    <div className="gpx">
      {/* Page head */}
      <div className="page-head">
        <div className="ph-ic"><MapPin /></div>
        <div>
          <h1>GPS Exceptions</h1>
          <div className="ph-sub">
            Every out-of-radius punch for the pay period in one place — spot repeat offenders and hot job sites,
            then inspect any punch on a map.
          </div>
        </div>
        <div className="ph-right">
          {selectedIdx >= 0 && (
            <div className="periodbox">
              <WeekPickerNav payPeriods={payPeriods} selectedIdx={selectedIdx} onChange={handlePeriodChange} />
            </div>
          )}
          <button type="button" className="btn" onClick={load} disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {loadError && (
        <div className="error-banner">
          <AlertTriangle />
          <span>{loadError}</span>
          <button type="button" className="btn sm eb-act" onClick={load}>Retry</button>
        </div>
      )}

      {showSkeleton ? (
        <>
          <KpiSkeleton />
          <TableSkeleton />
        </>
      ) : (
        <>
          {/* KPI strip — always period-wide, never filtered */}
          <div className="kpis">
            <div className="kpi total">
              <div className="kl"><AlertTriangle />Total Exceptions</div>
              <div className="kv">{totalExceptions}</div>
            </div>
            <div className="kpi emp">
              <div className="kl"><Users />Employees Affected</div>
              <div className="kv">{employeesAffected}</div>
            </div>
            <div className="kpi jobs">
              <div className="kl"><Building2 />Job Sites Affected</div>
              <div className="kv">{jobSitesAffected}</div>
              {hasPieceWork && (
                <div className="kpi-note"><Package />piece-work punches also present</div>
              )}
            </div>
            <div className="kpi split">
              <div className="kl"><Clock />Clock-in / Clock-out</div>
              <div className="splitval">
                <span className="sv in">{clockInCount}</span>
                <span className="sx">/</span>
                <span className="sv out">{clockOutCount}</span>
              </div>
              <div className="splitlab"><b className="in">In</b><b className="out">Out</b></div>
              <div className="kpi-note">one punch can flag both sides</div>
            </div>
          </div>

          {/* Hot-site callout */}
          {hotSite && (
            <div className="callout">
              <div className="co-ic"><AlertTriangle /></div>
              <div className="co-text">
                <b>{hotSite.name}</b> flagged <b>{hotSite.count} different employees</b> this period
                {' '}({hotSite.altProjectID ?? '—'}{hotSite.radiusMiles != null ? `, ${hotSite.radiusMiles} mi radius` : ''}).
                Likely a wrong job site location or a geofence that needs widening — worth a look before payroll.
              </div>
              <button
                type="button"
                className="co-jump"
                onClick={() => setJobFilter(hotSite.projectID)}
              >
                Show this site <ArrowRight />
              </button>
            </div>
          )}

          {/* Filter bar — row 1: search full-width; row 2: dropdown filters + segmented controls */}
          <div className="filterbar">
            <div className="filterbar-row1">
              <div className="searchwrap">
                <Search className="ic" />
                <input
                  type="text"
                  placeholder="Search employee, job, or message…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="revfilt-wrap">
                <div className="segmented" role="group" aria-label="Review status">
                  <button
                    type="button"
                    className={reviewFilter === 'unreviewed' ? 'on' : ''}
                    onClick={() => setReviewFilter('unreviewed')}
                  >
                    Unreviewed
                    <span className={`revfilt-badge ${unreviewedCount > 0 ? 'warn' : 'ok'}`}>{unreviewedCount}</span>
                  </button>
                  <button
                    type="button"
                    className={`rev${reviewFilter === 'reviewed' ? ' on' : ''}`}
                    onClick={() => setReviewFilter('reviewed')}
                  >
                    <Check />Reviewed
                    <span className={`revfilt-badge ${reviewedCount > 0 ? 'ok' : 'dim'}`}>{reviewedCount}</span>
                  </button>
                  <button
                    type="button"
                    className={reviewFilter === 'all' ? 'on' : ''}
                    onClick={() => setReviewFilter('all')}
                  >
                    All
                  </button>
                </div>
              </div>
            </div>

            <div className="filterbar-row2">
              <FloatMenuButton
                label="EMPLOYEE"
                valueLabel={empValueLabel}
                isPlaceholder={empFilter == null}
                isActive={empFilter != null}
                options={empMenuOptions}
                selectedValue={empFilter == null ? '' : String(empFilter)}
                onSelect={(v) => setEmpFilter(v === '' ? null : Number(v))}
                searchable
              />

              <FloatMenuButton
                label="JOB"
                valueLabel={jobValueLabel}
                isPlaceholder={jobFilter == null}
                isActive={jobFilter != null}
                options={jobMenuOptions}
                selectedValue={jobFilter == null ? '' : String(jobFilter)}
                onSelect={(v) => setJobFilter(v === '' ? null : v === '__piece' ? '__piece' : Number(v))}
                searchable
              />

              <FloatMenuButton
                label="LOCATION"
                valueLabel={locValueLabel}
                isPlaceholder={locFilter == null}
                isActive={locFilter != null}
                options={locMenuOptions}
                selectedValue={locFilter == null ? '' : String(locFilter)}
                onSelect={(v) => setLocFilter(v === '' ? null : v === '__none' ? '__none' : Number(v))}
              />

              <FloatMenuButton
                label="SUPERVISOR"
                valueLabel={supValueLabel}
                isPlaceholder={supFilter == null}
                isActive={supFilter != null}
                options={supMenuOptions}
                selectedValue={supFilter == null ? '' : String(supFilter)}
                onSelect={(v) => setSupFilter(v === '' ? null : v === '__none' ? '__none' : Number(v))}
                searchable
              />

              <div className="segmented" role="group" aria-label="Exception side">
                <button
                  type="button"
                  className={sideFilter === 'all' ? 'on' : ''}
                  onClick={() => setSideFilter('all')}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`in${sideFilter === 'in' ? ' on' : ''}`}
                  onClick={() => setSideFilter('in')}
                >
                  <LogIn />Clock In
                </button>
                <button
                  type="button"
                  className={`out${sideFilter === 'out' ? ' on' : ''}`}
                  onClick={() => setSideFilter('out')}
                >
                  <LogOut />Clock Out
                </button>
              </div>

              {anyFilterActive && (
                <button type="button" className="clear-btn" onClick={clearFilters}><X />Clear</button>
              )}
            </div>
          </div>

          {/* Result line */}
          <div className="resultline">
            <span><b>{filteredRows.length}</b> of {totalExceptions} exception{totalExceptions === 1 ? '' : 's'}</span>
            {anyFilterActive && <span className="filt-note">· filtered · KPIs stay period-wide</span>}
          </div>

          {/* Table / states — the affirming empty state must never show for a failed load */}
          {totalExceptions === 0 && loadError ? null : totalExceptions === 0 ? (
            <div className="state good">
              <div className="st-ic"><CheckCircle2 /></div>
              <h3>No GPS exceptions this pay period</h3>
              <p>Every punch this period was inside its job site's geofence. Nothing to review.</p>
            </div>
          ) : reviewFilter === 'unreviewed' && !otherFiltersActive && filteredRows.length === 0 ? (
            <div className="state good">
              <div className="st-ic"><CheckCircle2 /></div>
              <h3>All caught up — nothing left to review</h3>
              <p>Every exception this period has been reviewed.</p>
              <div className="st-act">
                <button type="button" className="btn" onClick={() => setReviewFilter('all')}>Show all exceptions</button>
              </div>
            </div>
          ) : reviewFilter === 'reviewed' && !otherFiltersActive && filteredRows.length === 0 ? (
            <div className="state zero">
              <div className="st-ic"><Inbox /></div>
              <h3>No reviewed exceptions yet</h3>
              <p>Mark an exception reviewed and it'll show up here.</p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="state zero">
              <div className="st-ic"><Inbox /></div>
              <h3>No exceptions match these filters</h3>
              <p>Try a different location, supervisor, employee, job, or search term.</p>
              <div className="st-act">
                <button type="button" className="btn" onClick={clearFilters}>Clear filters</button>
              </div>
            </div>
          ) : (
            <div className={`table-card${showRefreshing ? ' refreshing' : ''}`}>
              <div className="table-wrap">
                <table className="xt">
                  <thead>
                    <tr>
                      <th className={`sortable${sortCaretClass('employee')}`} onClick={() => handleSort('employee')}>
                        Employee<span className="sortcar" />
                      </th>
                      <th className={`sortable${sortCaretClass('time')}`} onClick={() => handleSort('time')}>
                        Date / Punch<span className="sortcar" />
                      </th>
                      <th className={`col-job-sub sortable${sortCaretClass('job')}`} onClick={() => handleSort('job')}>
                        Job / Site<span className="sortcar" />
                      </th>
                      <th>Flagged</th>
                      <th>Exception Message</th>
                      <th className={`col-dist sortable${sortCaretClass('distance')}`} onClick={() => handleSort('distance')}>
                        From Site<span className="sortcar" />
                      </th>
                      <th className="col-map">Map</th>
                      <th className="col-review">Reviewed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => {
                      const punchDate = r.inDate ?? r.outDate;
                      const msgParts = [
                        r.inException ? r.inExceptionMessage : null,
                        r.outException ? r.outExceptionMessage : null,
                      ].filter((m): m is string => !!m);
                      const joinedMsg = msgParts.join(' · ');
                      const dist = r.distMiles != null ? formatDist(r.distMiles) : null;
                      return (
                        <tr
                          key={r.punchExceptionID}
                          className={r.hasCoords ? undefined : 'noclick'}
                          onClick={r.hasCoords ? () => setMapRowID(r.punchExceptionID) : undefined}
                        >
                          <td>
                            <div className="emp-cell">
                              <div className="av" style={{ background: avatarColor(String(r.employeeID)) }}>
                                {initials(r.employeeName)}
                              </div>
                              <div>
                                <div className="en">
                                  {r.employeeName}
                                  {r.repeat && <span className="repeat"><RefreshCw />repeat</span>}
                                </div>
                                <div className="etitle">{r.jobTitle || '—'} · <span className="eid mono">{r.altEmployeeID ?? '—'}</span></div>
                              </div>
                            </div>
                          </td>
                          <td className="dt-cell">
                            <div className="d mono">{punchDate ? formatDateMono(punchDate) : '—'}</div>
                            <div className="t">
                              {r.inDate && <span><LogIn />{formatClock(r.inDate)}</span>}
                              {r.outDate ? (
                                <span><LogOut />{formatClock(r.outDate)}</span>
                              ) : r.inDate ? (
                                <span className="open-tag">shift open</span>
                              ) : null}
                            </div>
                          </td>
                          <td className="job-cell col-job-sub">
                            {r.projectID == null ? (
                              <>
                                <span className="piecework-tag"><Package />Piece work</span>
                                <div className="jid mono" style={{ marginTop: 4 }}>no job / geofence</div>
                              </>
                            ) : (
                              <>
                                <div className="jn">{r.projectName ?? '—'}</div>
                                <div className="jid mono">{r.altProjectID ?? '—'}</div>
                                {r.hotJob && <span className="hotdot"><AlertTriangle />Hot site</span>}
                              </>
                            )}
                          </td>
                          <td>
                            <div className="sides">
                              {r.inException && <span className="sbadge in"><LogIn />In</span>}
                              {r.outException && <span className="sbadge out"><LogOut />Out</span>}
                            </div>
                          </td>
                          <td className="msg-cell">
                            {msgParts.length === 0 ? (
                              <div className="msg none">No message recorded</div>
                            ) : (
                              <div className="msg" title={joinedMsg}>{joinedMsg}</div>
                            )}
                          </td>
                          <td className="dist-cell col-dist">
                            {dist ? (
                              <>
                                <div className={`dv mono ${dist.tone}`}>{dist.v}</div>
                                {r.radiusMiles != null && <div className="da">allowed {r.radiusMiles} mi</div>}
                              </>
                            ) : (
                              <div className="dv na mono">—</div>
                            )}
                          </td>
                          <td className="col-map">
                            {r.hasCoords ? (
                              <button
                                type="button"
                                className="mapbtn"
                                title="View on map"
                                onClick={(e) => { e.stopPropagation(); setMapRowID(r.punchExceptionID); }}
                              >
                                <MapPin />
                              </button>
                            ) : (
                              // No coordinates → no map affordance at all (§7); the quick
                              // popup from the Reviewed column covers notes for these rows.
                              <span className="noloc">no location</span>
                            )}
                          </td>
                          <td className="col-review" onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const pending = !!reviewPending[r.punchExceptionID];
                              const rowErr = reviewRowError[r.punchExceptionID];
                              const confirming = confirmUndoID === r.punchExceptionID;
                              // Reviewer + timestamp + note preview live in the pill's hover
                              // title only (§2b) — the dense row stays compact.
                              const revAtTip = r.reviewed
                                ? `Reviewed by ${r.reviewedByName ?? 'Unknown'}${r.reviewedAt ? ` · ${formatReviewedAt(r.reviewedAt)}` : ''}${r.reviewNote ? ` — "${r.reviewNote}"` : ''}`
                                : undefined;
                              if (confirming) {
                                return (
                                  <div className="rev-confirm">
                                    <span className="rc-txt">Undo review?</span>
                                    <button
                                      type="button"
                                      className="rc-yes"
                                      onClick={() => { setConfirmUndoID(null); toggleReviewInline(r.punchExceptionID, true); }}
                                    >
                                      Yes
                                    </button>
                                    <button
                                      type="button"
                                      className="rc-cancel"
                                      onClick={() => setConfirmUndoID(null)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                );
                              }
                              // Pill + 30×30 toggle sit in a fixed-width grid track so the
                              // toggles align vertically down the column (§2b).
                              return (
                                <div className="rev-cell">
                                  {r.reviewed ? (
                                    <span className="revpill yes" title={revAtTip}>
                                      Reviewed
                                      {r.reviewNote && <StickyNote />}
                                    </span>
                                  ) : (
                                    <span className="revpill no mono">—</span>
                                  )}
                                  <button
                                    type="button"
                                    className={`rev-tgl ${r.reviewed ? 'undo' : 'mark'}`}
                                    title={r.reviewed ? 'Un-review' : 'Mark reviewed…'}
                                    disabled={pending}
                                    onClick={() => {
                                      if (r.reviewed) setConfirmUndoID(r.punchExceptionID);
                                      else setNotePopupID(r.punchExceptionID);
                                    }}
                                  >
                                    {pending ? <RefreshCw className="spin" /> : r.reviewed ? <Undo2 /> : <Check />}
                                  </button>
                                  {rowErr && (
                                    <button
                                      type="button"
                                      className="rev-err"
                                      title={rowErr}
                                      onClick={() => toggleReviewInline(r.punchExceptionID, r.reviewed)}
                                    >
                                      Couldn't save · Retry
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {mapRow && (
        <GpsExceptionMapModal
          row={mapRow}
          onClose={() => setMapRowID(null)}
          onSubmitReview={submitReview}
        />
      )}

      {notePopupRow && !notePopupRow.reviewed && (
        <MarkReviewedPopup
          row={notePopupRow}
          onClose={() => setNotePopupID(null)}
          onSubmit={(id, note) => submitReview(id, true, note)}
        />
      )}
    </div>
  );
};

export default GpsExceptionsPage;
